import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { History, Plus } from 'lucide-react';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Textarea } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { apiError, type TimeEntry, type TimePunch } from './lib';
import CorrectionDialog from './CorrectionDialog';

/**
 * One timesheet: its punches, its exceptions, and the approve/reject/amend
 * actions a manager takes on it.
 *
 * The punch list renders every row the API returns, superseded ones
 * included, each labelled with what replaced it — the append-only ledger
 * ADR-0008 §5 describes, made visible rather than collapsed to "the current
 * answer". A correction never edits a row in place here; it opens
 * `CorrectionDialog`, which posts a NEW punch.
 */
export default function TimesheetDetailDialog({
  entryId,
  onClose,
  onChanged,
}: {
  entryId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const canApprove = can('attendance.approve');
  const canCorrect = can('attendance.correct');

  const [entry, setEntry] = useState<TimeEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [busy, setBusy] = useState(false);
  const [correcting, setCorrecting] = useState<TimePunch | 'missing' | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<TimeEntry>(`/attendance/entries/${entryId}`)
      .then(({ data }) => setEntry(data))
      .catch((e) => setError(apiError(e, t('workforce.timesheets.loadFailed'))))
      .finally(() => setLoading(false));
  }, [entryId, t]);

  useEffect(load, [load]);

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/attendance/entries/${entryId}/approve`, {});
      toast.success(t('workforce.timesheets.approved'));
      load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('workforce.timesheets.approveFailed')));
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!rejectReason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/attendance/entries/${entryId}/reject`, { reason: rejectReason.trim() });
      toast.success(t('workforce.timesheets.rejected'));
      setShowReject(false);
      setRejectReason('');
      load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('workforce.timesheets.rejectFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('workforce.timesheets.detailTitle')}
      className="max-w-2xl"
      footer={
        <Button variant="outline" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error || !entry ? (
        <ErrorState message={error ?? t('workforce.timesheets.notFound')} onRetry={load} />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-semibold">
                {entry.employee.firstName} {entry.employee.lastName ?? ''}
                <span className="ms-2 text-sm font-normal text-ink-muted">{entry.employee.employeeCode}</span>
              </p>
              <p className="text-sm text-ink-muted">
                {entry.branch.name} · {formatDateTime(entry.businessDate, i18n.language)}
              </p>
            </div>
            <Badge tone={toneForStatus(entry.status)}>{t(`workforce.timeEntryStatus.${entry.status}`)}</Badge>
          </div>

          {entry.autoClosed && (
            <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
              {t('workforce.timesheets.autoClosedNote')}
            </p>
          )}

          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
            <Stat label={t('workforce.timesheets.worked')} minutes={entry.workedMinutes} />
            <Stat label={t('workforce.timesheets.break')} minutes={entry.breakMinutes} />
            <Stat label={t('workforce.timesheets.late')} minutes={entry.lateMinutes} />
            <Stat label={t('workforce.timesheets.overtime')} minutes={entry.overtimeMinutes} />
          </dl>

          {entry.exceptions.length > 0 && (
            <div className="space-y-1.5">
              <h4 className="text-sm font-semibold">{t('workforce.timesheets.exceptions')}</h4>
              <ul className="flex flex-wrap gap-1.5">
                {entry.exceptions.map((exc) => (
                  <li key={exc.id}>
                    <Badge tone={exc.resolvedAt ? 'neutral' : 'warning'}>
                      {t(`workforce.exceptionType.${exc.type}`)}
                    </Badge>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <section>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="flex items-center gap-1.5 text-sm font-semibold">
                <Icon as={History} size="sm" />
                {t('workforce.timesheets.punches')}
              </h4>
              {canCorrect && (
                <Button variant="outline" size="sm" onClick={() => setCorrecting('missing')}>
                  <Icon as={Plus} size="sm" className="me-1.5" />
                  {t('workforce.correction.addMissing')}
                </Button>
              )}
            </div>
            {entry.punches.length === 0 ? (
              <EmptyState title={t('workforce.timesheets.noPunches')} />
            ) : (
              <ul className="divide-y rounded-lg border text-sm">
                {entry.punches.map((punch) => (
                  <li key={punch.id} className="flex items-center justify-between gap-2 p-2.5">
                    <div>
                      <p>
                        {t(`workforce.punchType.${punch.type}`)}
                        {punch.supersededById && (
                          <Badge tone="neutral" className="ms-2">
                            {t('workforce.correction.superseded')}
                          </Badge>
                        )}
                        {punch.supersedesId && (
                          <Badge tone="info" className="ms-2">
                            {t('workforce.correction.amendment')}
                          </Badge>
                        )}
                      </p>
                      <p className="text-xs text-ink-muted">
                        {formatDateTime(punch.occurredAt, i18n.language)}
                        {' · '}
                        {t(`workforce.punchSource.${punch.source}`)}
                      </p>
                      {punch.correctionReason && (
                        <p className="text-xs italic text-ink-muted">"{punch.correctionReason}"</p>
                      )}
                    </div>
                    {canCorrect && !punch.supersededById && (
                      <Button variant="ghost" size="sm" onClick={() => setCorrecting(punch)}>
                        {t('workforce.correction.amend')}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {canApprove && entry.status === 'PENDING_APPROVAL' && (
            <section className="space-y-2 rounded-lg border p-3">
              {!showReject ? (
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowReject(true)}>
                    {t('workforce.timesheets.reject')}
                  </Button>
                  <Button size="sm" disabled={busy} onClick={() => void approve()}>
                    {t('workforce.timesheets.approve')}
                  </Button>
                </div>
              ) : (
                <>
                  <Field label={t('workforce.timesheets.rejectReason')} required>
                    <Textarea rows={2} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                  </Field>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setShowReject(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={busy || !rejectReason.trim()}
                      onClick={() => void reject()}
                    >
                      {t('workforce.timesheets.confirmReject')}
                    </Button>
                  </div>
                </>
              )}
            </section>
          )}

          {entry.rejectionReason && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {t('workforce.timesheets.rejectedNote', { reason: entry.rejectionReason })}
            </p>
          )}
        </div>
      )}

      {correcting && entry && (
        <CorrectionDialog
          entry={entry}
          punch={correcting === 'missing' ? null : correcting}
          onClose={() => setCorrecting(null)}
          onSaved={() => {
            setCorrecting(null);
            load();
            onChanged();
          }}
        />
      )}
    </Dialog>
  );
}

function Stat({ label, minutes }: { label: string; minutes: number }) {
  const { t } = useTranslation();
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide rtl:tracking-normal text-ink-muted">{label}</dt>
      <dd className="tabular-nums font-medium">{t('workforce.timesheets.minutes', { count: minutes })}</dd>
    </div>
  );
}
